import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { parseLocalDate } from '../../../utils/dateUtils';

const UpcomingDeadlines = ({ events, onEventClick }) => {
  const getUpcomingEvents = () => {
    const now = new Date();
    const upcoming = events?.filter(event => parseLocalDate(event.date) >= now)?.sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date))?.slice(0, 5);

    return upcoming?.map(event => ({
      ...event,
      daysRemaining: Math.ceil((parseLocalDate(event.date) - now) / (1000 * 60 * 60 * 24)),
      urgencyLevel: (() => {
        const days = Math.ceil((parseLocalDate(event.date) - now) / (1000 * 60 * 60 * 24));
        if (days <= 3) return 'critical';
        if (days <= 7) return 'urgent';
        if (days <= 14) return 'warning';
        return 'normal';
      })()
    }));
  };

  const getUrgencyColor = (level) => {
    const colors = {
      critical: 'border-red-500 bg-red-50',
      urgent: 'border-orange-500 bg-orange-50',
      warning: 'border-yellow-500 bg-yellow-50',
      normal: 'border-border bg-card'
    };
    return colors?.[level] || colors?.normal;
  };

  const getUrgencyTextColor = (level) => {
    const colors = {
      critical: 'text-red-600',
      urgent: 'text-orange-600',
      warning: 'text-yellow-600',
      normal: 'text-muted-foreground'
    };
    return colors?.[level] || colors?.normal;
  };

  const getEventTypeColor = (type) => {
    const colors = {
      applications: 'bg-blue-500',
      exams: 'bg-red-500',
      results: 'bg-green-500',
      interviews: 'bg-purple-500',
      registration: 'bg-orange-500'
    };
    return colors?.[type] || 'bg-gray-500';
  };

  const formatTimeRemaining = (days) => {
    if (days === 0) return "Aujourd'hui";
    if (days === 1) return "Demain";
    if (days <= 7) return `${days} jours`;
    if (days <= 30) return `${Math.ceil(days / 7)} semaines`;
    return `${Math.ceil(days / 30)} mois`;
  };

  const upcomingEvents = getUpcomingEvents();

  return (
    <div className="bg-card border border-border rounded-lg shadow-soft">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="font-medium text-foreground">Échéances à venir</h3>
        <Link
          to="/student-profile?tab=alerts"
          className="text-sm text-primary hover:text-primary/80 transition-smooth"
        >
          Gérer les alertes
        </Link>
      </div>
      <div className="p-4">
        {upcomingEvents?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h4 className="font-medium text-foreground mb-2">
              Aucune échéance proche
            </h4>
            <p className="text-sm text-muted-foreground mb-4">
              Explorez les établissements pour découvrir les opportunités.
            </p>
            <Link to="/post-bac-database">
              <Button variant="outline" size="sm">
                Explorer les établissements
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {upcomingEvents?.map((event, index) => (
              <button
                key={index}
                onClick={() => onEventClick(event)}
                className={`w-full text-left p-3 rounded-lg border transition-smooth hover:shadow-soft ${
                  getUrgencyColor(event?.urgencyLevel)
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className={`w-2 h-2 rounded-full ${getEventTypeColor(event?.type)}`} />
                      <span className="text-xs font-medium text-primary">
                        {event?.typeLabel}
                      </span>
                      {event?.urgencyLevel === 'critical' && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                          Critique
                        </span>
                      )}
                    </div>
                    <h4 className="font-medium text-foreground text-sm mb-1">
                      {event?.title}
                    </h4>
                    <p className="text-xs text-muted-foreground mb-2">
                      {event?.institution}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        {parseLocalDate(event.date)?.toLocaleDateString('fr-FR')}
                      </span>
                      <span className={`text-xs font-medium ${getUrgencyTextColor(event?.urgencyLevel)}`}>
                        {formatTimeRemaining(event?.daysRemaining)}
                      </span>
                    </div>
                  </div>
                  <Icon name="ChevronRight" size={16} className="text-muted-foreground ml-2" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="p-4 border-t border-border bg-muted/50">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {upcomingEvents?.length} échéance{upcomingEvents?.length !== 1 ? 's' : ''} à venir
          </span>
          <Link
            to="/student-profile?tab=notifications"
            className="text-primary hover:text-primary/80 transition-smooth"
          >
            Configurer les notifications
          </Link>
        </div>
      </div>
    </div>
  );
};

export default UpcomingDeadlines;